import React from 'react';
import { useLocation } from 'react-router-dom';
import NotFoundScreen from './index';

// Concrete route paths (excluding parameterised ones) enumerated for
// exact-match checks. Parameterised routes are matched separately by
// regex below. Keep in sync when a Route is added to navigation/index.js.
//
// This drives the 404 fallback (D-03). It exists outside <Switch>
// because the top-level router in navigation/index.js renders routes
// in a Fragment tree, not a Switch, and refactoring that is out of
// scope for this fix.
const KNOWN_EXACT_PATHS = new Set([
  '/', '/login', '/forgot-password', '/privacy', '/terms', '/verify-audit',
  '/dashboard', '/claims', '/claims/new', '/policies', '/offers', '/invoicing',
  '/brokers', '/risk-carriers', '/compliance-reports', '/data-residency',
  '/dsar-requests', '/api-keys', '/webhooks', '/workflow-rules',
  '/claims-analytics', '/demo', '/unmatched-documents', '/account-settings',
  '/team-management', '/clients', '/clients/new', '/offers/new', '/policies/new',
  '/offers/import',
  '/risk-signals', '/ai-governance', '/bordereaux',
  '/reinsurance/submissions', '/reinsurance/submissions/intake',
  '/reinsurance/deals', '/reinsurance/placement', '/reinsurance/placement/queue',
  '/reinsurance/closings', '/reinsurance/claims', '/reinsurance/portfolio',
  '/reinsurance/fx-rates', '/reinsurance/sanctions',
  // Reinsurance second wave. '/reinsurance' is the module landing page.
  '/reinsurance',
  '/reinsurance/treaties', '/reinsurance/accounting', '/reinsurance/recoveries',
  '/reinsurance/counterparties', '/reinsurance/compliance', '/reinsurance/renewals',
  '/platform', '/platform/tenants', '/orgs/onboard',
  '/portal/my-policies', '/portal/my-claims', '/portal/my-clients',
  '/portal/my-commissions', '/portal/my-documents', '/portal/my-profile',
  '/commissions', '/secure-files', '/sign-document', '/share-file',
  '/orgs-admin', '/users', '/audit-logs', '/audit-integrity', '/integrations',
  '/website-forms', '/chat', '/chat-requests', '/group-video-call',
  '/video-transcript', '/e-signatures', '/email', '/my-profile', '/Error',
]);

// Path patterns for routes with URL params (e.g. /claims/:id).
const KNOWN_PATH_PATTERNS = [
  /^\/claims\/[a-f0-9]{24}$/i,
  /^\/policies\/[a-f0-9]{24}$/i,
  /^\/clients\/[a-f0-9]{24}$/i,
  /^\/reinsurance\/deals\/[a-f0-9]{24}$/i,
  /^\/reinsurance\/deals\/[a-f0-9]{24}\/placement$/i,
  /^\/reinsurance\/claims\/[a-f0-9]{24}$/i,
  /^\/reinsurance\/endorsements\/[a-f0-9]{24}$/i,
  // Reinsurance second wave — one per detail screen.
  /^\/reinsurance\/treaties\/[a-f0-9]{24}$/i,
  /^\/reinsurance\/accounting\/[a-f0-9]{24}$/i,
  /^\/reinsurance\/recoveries\/[a-f0-9]{24}$/i,
  /^\/reinsurance\/counterparties\/[a-f0-9]{24}$/i,
  /^\/reinsurance\/renewals\/[a-f0-9]{24}$/i,
  /^\/platform\/tenants\/[a-f0-9]{24}$/i,
  /^\/orgs\/[a-f0-9]{24}$/i,
  // Portal-scoped detail routes. The parameterised entries were missing
  // — MyPolicyDetailScreen rendered fine from the <Route>, but this gate
  // rendered <NotFoundScreen /> alongside it because /portal/my-*/[id]
  // didn't match any known path (2026-08-06: broker anjani-testbroker
  // hit 404 on a real policy detail URL). Add matching patterns for
  // every portal detail screen that lives under /portal/my-*/:id.
  /^\/portal\/my-policies\/[a-f0-9]{24}$/i,
  /^\/portal\/my-claims\/[a-f0-9]{24}$/i,
  /^\/e-signatures\/[a-f0-9]{24}$/i,
  /^\/files\/.+$/,
  /^\/guest-video\/.+$/,
  /^\/sign\/.+$/,
  /^\/make\/[^/]+\/[^/]+\/[^/]+\/[^/]+\/[^/]+$/,
  /^\/group-video-call\/.+$/,
];

// A trailing slash is the same page. react-router matches `exact` paths
// without `strict`, so /reinsurance/ renders the /reinsurance route — but the
// lookups below are exact string and regex comparisons, which did not, so the
// screen rendered with a 404 drawn on top of it. Normalise once, here, rather
// than adding a second entry per route for the slashed spelling.
const normalise = (p) => (p.length > 1 && p.endsWith('/') ? p.replace(/\/+$/, '') : p);

const NotFoundGate = () => {
  const location = useLocation();
  const path = normalise(location.pathname);
  if (KNOWN_EXACT_PATHS.has(path)) return null;
  if (KNOWN_PATH_PATTERNS.some(re => re.test(path))) return null;
  return <NotFoundScreen />;
};

export default NotFoundGate;
